import { useMemo, useState, useEffect } from 'react';
import { DeviceType_Enum, ListDevice } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { useQueryClient } from 'react-query';
import { DevicesApi, DevicesQueries, DevicesQueryKey } from '@frontend/api-devices';
import { E911QueryKeys } from '@frontend/api-e911-addresses';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { ModalControlModalProps, Table, Tray, useAlert, ContentLoader } from '@frontend/design-system';
import { logError } from '../../../utils/loggers';

type Props = ModalControlModalProps & {
  initalDeviceIds: Record<string, boolean>;
  e911Name: string;
  e911Address: string;
  e911Id: string;
};

export const EditDeviceAssignmentsToE911 = ({
  initalDeviceIds,
  e911Name,
  e911Address,
  e911Id,
  ...modalProps
}: Props) => {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });
  const [filteredGlobalData, setFilteredGlobalData] = useState<ListDevice[] | null>(null);
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const alerts = useAlert();

  const { data: { devices } = { devices: [] } } = DevicesQueries.useGetDevicesList({
    tenantId,
  });
  const filteredDevices = useMemo(
    () =>
      devices?.filter(
        (device) => device.type === DeviceType_Enum.DESK_PHONE || device.type === DeviceType_Enum.SOFTPHONE
      ),
    [devices]
  );
  const [selectedDevices, setSelectedDevices] = useState<Record<string, boolean>>();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!modalProps.show) {
      setFilteredGlobalData(null);
      setSelectedDevices(undefined);
    }
  }, [modalProps.show]);

  const { mutateAsync: updateAddress, isLoading } = useMutation({
    mutationFn: (deviceIds: string[]) =>
      DevicesApi.BatchUpdateDevices({
        tenantId,
        deviceIds,
        e911AddressId: e911Id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([tenantId, ...DevicesQueryKey.queryKeys.devicesList()]);
      queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
      modalProps.onClose();
    },
    onError: (e) => {
      console.error('Unable to apply E911 address to one or more devices', e);
      logError('EditDeviceAssignmentsToE911:updateAddressMutation | Failed to apply E911 address to devices', {
        data: { tenantId, e911Id, error: e },
      });
      alerts.error(
        t(
          'Unable to apply E911 address to one or more devices. The address may be invalid. Please verify or contact Weave for help.'
        )
      );
    },
  });

  const onSave = () => {
    if (!selectedDevices) return;
    const filteredIds = Object.keys(selectedDevices);
    updateAddress(filteredIds).then(() => {
      filteredIds.forEach((deviceId) => {
        queryClient.invalidateQueries([deviceId, ...DevicesQueryKey.queryKeys.deviceAddressById()]);
      });
      alerts.success(t('E911 address applied to {{count}} devices', { count: filteredIds.length }));
    });
  };

  return (
    <>
      <Tray {...modalProps} width='medium'>
        <SurfaceHeader.Root noBorder css={{ margin: theme.spacing(-4, -4, 0, -4), width: 'calc(100% + 64px)' }}>
          <SurfaceHeader.Row>
            <SurfaceHeader.Title>{t('Devices Using {{name}}', { name: e911Name })}</SurfaceHeader.Title>
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon iconName='x' onClick={modalProps.onClose} />
            </SurfaceHeader.Actions>
          </SurfaceHeader.Row>
          <SurfaceHeader.Body>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 'var(--fabric-typography-body-sm-font-size)',
                gap: 'var(--fabric-space-gap-xs)',
                color: 'var(--fabric-color-neutral-100)',
              }}
            >
              <Icon name='location-small' />
              {e911Address}
            </div>
          </SurfaceHeader.Body>
        </SurfaceHeader.Root>
        <Tray.Body>
          <ContentLoader show={isLoading} />
          <Table
            data={filteredGlobalData ?? filteredDevices}
            isPaginated
            isSelectable
            hasGlobalSearch
            uniqueRowId={(device) => device.deviceId}
            rowSelectionConfig={{
              initialState: initalDeviceIds,
              onSelectionToggle: (_, __, selectedIds) => {
                setSelectedDevices(selectedIds);
              },
              isRowDisabled: (rowData) => {
                return Object.keys(initalDeviceIds).includes(rowData.deviceId);
              },
            }}
            globalSearchConfig={{
              label: t('Search'),
              searchHandler: (term) => {
                if (!term?.trim()) {
                  setFilteredGlobalData(null);
                  return;
                }
                const filteredContacts = filteredDevices?.filter(({ name }) =>
                  name.toLocaleLowerCase().includes(term.toLocaleLowerCase())
                );
                setFilteredGlobalData(filteredContacts ?? null);
              },
            }}
            colConfig={[
              {
                Header: t('Device Name'),
                id: 'contact-list-name',
                accessor: (device) => device.name,
              },
            ]}
          />
        </Tray.Body>
        <Tray.Actions
          onSecondaryClick={modalProps.onClose}
          secondaryLabel='Cancel'
          primaryLabel='Save'
          primaryTrackingId='assign-devices-directories'
          disabledPrimary={!selectedDevices}
          onPrimaryClick={onSave}
        />
      </Tray>
    </>
  );
};
